/**
 * Auth Events - Decoupled auth event bus
 *
 * GW-2099: Provides a simple event mechanism for auth-related events
 * without creating circular dependencies between API clients and the store.
 *
 * Usage:
 *   Emit:    authEvents.emit('session-expired')
 *   Listen:  authEvents.on('session-expired', handler)
 *   Remove:  authEvents.off('session-expired', handler)
 */

type AuthEventType = 'session-expired';
type AuthEventHandler = () => void;

const listeners = new Map<AuthEventType, Set<AuthEventHandler>>();

export const authEvents = {
  on(event: AuthEventType, handler: AuthEventHandler): void {
    // GW-6126: Defensive guard — reject non-function listeners at registration
    if (typeof handler !== 'function') {
      console.warn(
        `[authEvents] Attempted to register non-function handler for "${event}":`,
        handler
      );
      return;
    }

    if (!listeners.has(event)) {
      listeners.set(event, new Set());
    }
    listeners.get(event)!.add(handler);
  },

  off(event: AuthEventType, handler: AuthEventHandler): void {
    listeners.get(event)?.delete(handler);
  },

  emit(event: AuthEventType): void {
    // GW-6126: Defensive guard — skip non-function entries during emit (defense in depth)
    listeners.get(event)?.forEach((handler) => {
      if (typeof handler !== 'function') {
        return;
      }
      handler();
    });
  },
};
